import React, { ReactNode, FC, ComponentProps } from 'react';
import Logo, { LogoComponentType } from 'app/components/svgs/Logo';
import { useCss, IStyles } from 'app/utils/css';
import { HEADER_HEIGHT } from 'app/constants/HeaderStyles';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { usePageBreakpoint } from 'app/components/layout/Breakpoint';
import { NumericBreakpoint } from 'app/constants/Breakpoints';
import { NavSpacer } from 'app/components/presentation/nav/NavToolbar';
import SquareLogo from 'app/components/svgs/SquareLogo';
import Navigation, { NavigationContext, NavigationType } from 'app/components/header/navigation/Navigation';
import NavigationSection from 'app/components/header/navigation/NavigationSection';
import { IRenderFunction } from 'app/utils/types';
import DiscreetErrorBoundary from '../utilities/DiscreetErrorBoundary';

export enum HeaderStack {
  CallContainer = 0,
  StickyContainer = 1,
  Main = 2,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Navigation = 3,
}

export interface IHeaderViewProps {
  /**
   * Specifies the desired navigation type that should be used in the header.
   * {@link NavigationType.Header} means that only items in header will be displayed (drawer or
   * dropdown nav will not be rendered).
   */
  navigationType: NavigationType;
  /**
   * Navigation items that will be displayed right after the {@link LogoComponent} when size allows,
   * or moved to the navigation menu (either dropdown or drawer) otherwise.
   *
   * Important: Use only header navigation items as root elements: {@link NavigationSection},
   * {@link NavigationItem}, or {@link NavigationLink}:
   * ```
   * <>
   *   <NavigationItem ... />
   *   <NavigationLink ... />
   * </>
   * ```
   */
  leftHeaderNavigationItems?: ReactNode;
  /**
   * Navigation items that will be displayed right before the {@link rightContent} when size allows,
   * or moved to the navigation menu (either dropdown or drawer) otherwise.
   *
   * Important: Use only header navigation items as root elements: {@link NavigationSection},
   * {@link NavigationItem}, or {@link NavigationLink}:
   * ```
   * <>
   *   <NavigationItem ... />
   *   <NavigationLink ... />
   * </>
   * ```
   */
  rightHeaderNavigationItems?: ReactNode;
  /**
   * Navigation items that will always be rendered in the navigation menu, or not rendered
   * at all if the {@link navigationType} is {@link NavigationType.Header}.
   *
   * Important: Use only header navigation items as root elements: {@link NavigationSection},
   * {@link NavigationItem}, or {@link NavigationLink}:
   * ```
   * <>
   *   <NavigationItem ... />
   *   <NavigationLink ... />
   * </>
   * ```
   */
  navigationItems?: ReactNode;
  /**
   * Arbitrary centered content.
   */
  centerContent?: ReactNode;
  /**
   * Arbitrary content that will always be displayed on the right regardless of the available space.
   * You have to control the available space yourself.
   */
  rightContent?: ReactNode;
  /**
   * A function (not a component!) that is called to render the link that wraps the
   * {@link LogoComponent} or {@link SquareLogoComponent}.
   */
  renderLogoWrapper?: IRenderFunction<{ children: ReactNode; className: string }>;
  LogoComponent?: LogoComponentType;
  SquareLogoComponent?: LogoComponentType;
  /**
   * Dev server style overrides. This is a workaround and is here only to extract the injected
   * build parameters out of this file in order not to crash Storybook.
   */
  _devServerOverrides?: IStyles;
}

/**
 * This is and should be a purely presentational component (breakpoints are considered presentation).
 * All logic should be extracted as props and moved to <Header>
 */
const HeaderView: FC<IHeaderViewProps> = ({
  navigationType,
  leftHeaderNavigationItems,
  rightHeaderNavigationItems,
  navigationItems,
  centerContent,
  rightContent,

  LogoComponent = Logo,
  SquareLogoComponent = HeaderSquareLogo,

  renderLogoWrapper = props => <div {...props} />,

  _devServerOverrides = {},
}) => {
  const css = useCss();
  const breakpoint = usePageBreakpoint();
  const isMobile = breakpoint < NumericBreakpoint.Wide;
  const logo =
    breakpoint < NumericBreakpoint.Medium ? <SquareLogoComponent height="34px" /> : <LogoComponent height="34px" />;

  const displayCenterContent = Boolean(centerContent);
  const displayLeftNavigationItemsInHeader = !isMobile && !displayCenterContent;
  const displayRightNavigationItemsInHeader = !isMobile;

  return (
    <NavigationContext.Provider value={{ type: NavigationType.Header }}>
      <div
        className={css('header', {
          position: 'relative',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          height: `${HEADER_HEIGHT}px`,
          fontSize: '12px',
          boxShadow: `0 1px 0 0 ${COLOR_PALETTE.grey.lighter}`,
          backgroundColor: COLOR_PALETTE.grey.white,
          zIndex: HeaderStack.Main,
          '@media print': {
            display: 'none',
          },
          ..._devServerOverrides,
        })}
      >
        <Navigation type={navigationType}>
          {!displayRightNavigationItemsInHeader && Boolean(rightHeaderNavigationItems) && (
            <NavigationSection>{rightHeaderNavigationItems}</NavigationSection>
          )}
          {!displayLeftNavigationItemsInHeader && Boolean(leftHeaderNavigationItems) && (
            <NavigationSection>{leftHeaderNavigationItems}</NavigationSection>
          )}
          {navigationItems}
        </Navigation>

        {renderLogoWrapper({
          children: logo,
          className: css('logoWrapper', {
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            justifyContent: 'center',
            margin: '0 1em',
          }),
        })}

        {displayLeftNavigationItemsInHeader && leftHeaderNavigationItems}

        {displayCenterContent ? (
          <>
            <NavSpacer />
            <DiscreetErrorBoundary name="NavigationCenterContent">
              {centerContent}
            </DiscreetErrorBoundary>
            <NavSpacer />
          </>
        ) : (
          <NavSpacer />
        )}

        {displayRightNavigationItemsInHeader && rightHeaderNavigationItems}

        <DiscreetErrorBoundary name="NavigationRightContent">
          {rightContent}
        </DiscreetErrorBoundary>
      </div>
    </NavigationContext.Provider>
  );
};

/**
 * A SquareLogo component that provides a reasonable Fallback for the header
 */
const HeaderSquareLogo: FC<ComponentProps<LogoComponentType>> = props => <SquareLogo Fallback={Logo} {...props} />;

export default HeaderView;
